import { Button } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { formatDate, BookingSessionViewModel } from '@/common/utils';
import { SurveyFeedbackStatus } from '@/constants';
import { CompanyNameWithDescription } from '@/common/components/company-name-with-description';
import styles from './styles.module.scss';

export enum UpcomingSessionStatuses {
  PENDING,
  VERIFIED,
  PERFORMED,
  CANCELLED,
}

type Props = {
  item: BookingSessionViewModel;
  className?: string;
  isAdvisor: boolean;
  feedbackProps: {
    feedbackStatus: API.SurveyFeedbackStatus;
    onFeedbackBtnClick: (s: BookingSessionViewModel) => void;
  };
  onRebuyBtnClick?: (s: BookingSessionViewModel) => void;
};

export function ReportSessionItem({
  item,
  isAdvisor,
  className = '',
  onRebuyBtnClick,
  feedbackProps,
}: Props) {
  const { t } = useTranslation();
  const bookingId = item.id;
  const showRebuyAction = !item.canBeRebook && isAdvisor && onRebuyBtnClick;

  const getFeedbackActionText = useCallback(() => {
    if (!isAdvisor) {
      return t('myFeedbacks.viewReportAction');
    }

    if (feedbackProps.feedbackStatus === SurveyFeedbackStatus.Completed) {
      return t('myFeedbacks.viewReportAction');
    }

    if (feedbackProps.feedbackStatus === SurveyFeedbackStatus.Draft) {
      return t('myFeedbacks.updateFeedbackAction');
    }

    return t('myFeedbacks.writeFeedbackAction');
  }, [feedbackProps.feedbackStatus, isAdvisor, t]);

  return (
    <div
      className={classNames(styles.item, styles.confirmed, className, {
        [styles.autoHeight]: showRebuyAction,
      })}
      data-dd-privacy="mask"
    >
      <div className={classNames(styles.statusColumn)}>
        <div className={styles.info}>{formatDate(item.startTime, 'DD MMM, HH.mm')}</div>
      </div>
      <div className={styles.contentColumn}>
        {isAdvisor && (
          <>
            <div className={styles.name}>{item.customerFullName}</div>
            {item.customerCompanyName && (
              <div className={styles.company}>
                <CompanyNameWithDescription
                  companyName={item.customerCompanyName}
                  companyId={item.companyId}
                />
              </div>
            )}
          </>
        )}
        {!isAdvisor && (
          <>
            <div className={styles.name}>{item.advisorName}</div>
          </>
        )}
      </div>

      <div className={styles.actionColumn}>
        {showRebuyAction && (
          <Button onClick={() => onRebuyBtnClick(item as BookingSessionViewModel)}>
            {t('rebuy.bookingForm.rebuyAction')}
          </Button>
        )}
        <Button
          className={styles.hasBookingId}
          type={
            feedbackProps.feedbackStatus !== SurveyFeedbackStatus.Completed ? 'primary' : 'default'
          }
          onClick={() => {
            feedbackProps.onFeedbackBtnClick(item);
          }}
        >
          {getFeedbackActionText()}
        </Button>

        {bookingId && <div className={styles.bookingIdWrapper}>(ID {bookingId})</div>}
      </div>
    </div>
  );
}
